import React, { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Typography, Box, createTheme, ThemeProvider, Switch } from '@mui/material';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import RecommendOutlinedIcon from '@mui/icons-material/RecommendOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import HowToVoteOutlinedIcon from '@mui/icons-material/HowToVoteOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import type { ISwitchWithCustomText } from '../Switch/SwitchWithCustomText';
import { IUserConfiguration, WebNotificationsSettings } from '../../redux/configuration/types';
import { useDispatch } from 'react-redux';
import { toggleAccountModal } from '../../redux/appConfigurator/action';
import { settings } from '../EsSettings/settings.data';

export interface ISettingsProps extends Omit<ISwitchWithCustomText, 'header' | 'body' | 'theme'> {
    onChange: (payload: any) => void;
    setting: IUserConfiguration;
}

const notificationRowIcon = (serviceName: string) => {
    const sx = { fontSize: 26 as const };
    switch (serviceName) {
        case 'application_activity':
            return <SubscriptionsOutlinedIcon sx={sx} />;
        case 'project_recommendation':
            return <RecommendOutlinedIcon sx={sx} />;
        case 'campaign_activities':
            return <CampaignOutlinedIcon sx={sx} />;
        case 'mentions':
            return <AlternateEmailOutlinedIcon sx={sx} />;
        case 'shared_content':
            return <ShareOutlinedIcon sx={sx} />;
        case 'vote_activity':
            return <HowToVoteOutlinedIcon sx={sx} />;
        case 'jury_activity':
            return <GavelOutlinedIcon sx={sx} />;
        default:
            return <SubscriptionsOutlinedIcon sx={sx} />;
    }
};

const AccountNotifications: React.FC<ISettingsProps> = props => {
    const { onChange, setting } = props;
    const [settingValue, setSettingsValue] = useState<any>();

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(toggleAccountModal({ on: false }));

        if (setting) {
            setSettingsValue({
                web_communication_preference: setting.web_communication_preference,
                web_notifications_settings: setting.web_notifications_settings,
                appConfig: {
                    show_app_guide: setting.show_app_guide,
                    show_introduction: setting.show_introduction,
                    show_project_guide: setting.show_project_guide,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);

    const switchTheme = createTheme({
        components: {
            MuiSwitch: {
                styleOverrides: {
                    switchBase: {
                        color: theme[themeStyle].tag_white,
                    },
                    colorPrimary: {
                        '&.Mui-checked': {
                            color: theme[themeStyle].tag_white,
                        },
                    },
                    track: {
                        opacity: 1,
                        backgroundColor: theme[themeStyle].gray_message,
                        '.Mui-checked.Mui-checked + &': {
                            opacity: 0.9,
                            backgroundColor: theme[themeStyle].highlight,
                        },
                    },
                },
            },
            //@ts-ignore
            custom: {},
        },
    });

    const handleOnToggle = (value: boolean, child: string, parent: string) => {
        setSettingsValue({
            ...settingValue,
            [parent]: {
                ...settingValue[parent],
                [child]: value,
            },
        });
        if (parent === 'appConfig') {
            onChange({
                [child]: value,
            });
        } else {
            onChange({
                ...settingValue,
                [parent]: {
                    ...settingValue[parent],
                    [child]: value,
                },
            });
        }
    };

    return (
        <ThemeProvider theme={switchTheme}>
            <Box
                style={styles.flexColumn}
                sx={{
                    gap: 3,
                    width: '100%',
                    height: '100%',
                    p: {
                        xs: 2,
                        sm: 0,
                    },
                }}>
                <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '0.8125rem',
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                color: theme[themeStyle].tag_white,
                                opacity: 0.65,
                                lineHeight: 1.5,
                                maxWidth: '36rem',
                            }}>
                            Choose when and how to be notified. Select push and email notifications that you&apos;d like
                            to receive.
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {settingValue &&
                            settingValue.web_notifications_settings &&
                            settings.map((set, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        flexWrap: 'wrap',
                                        p: 3,
                                        borderRadius: 3,
                                        border: '1px solid rgba(255,255,255,0.06)',
                                        bgcolor: 'rgba(39, 39, 42, 0.35)',
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': { bgcolor: 'rgba(39, 39, 42, 0.5)' },
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 2,
                                                flexShrink: 0,
                                                bgcolor: 'rgba(0,0,0,0.28)',
                                                border: '1px solid rgba(255,255,255,0.06)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: theme[themeStyle].highlight,
                                            }}>
                                            {notificationRowIcon(set.service_name)}
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    fontWeight: 700,
                                                    fontSize: '0.9375rem',
                                                    color: theme[themeStyle].primary_white,
                                                    mb: 0.25,
                                                }}>
                                                {set.header}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    color: theme[themeStyle].tag_white,
                                                    opacity: 0.65,
                                                    lineHeight: 1.4,
                                                }}>
                                                {set.body}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Switch
                                        focusVisibleClassName=".Mui-focusVisible"
                                        disableRipple
                                        checked={Boolean(
                                            settingValue.web_notifications_settings[
                                                set.service_name as keyof WebNotificationsSettings
                                            ],
                                        )}
                                        onChange={e =>
                                            handleOnToggle(e.target.checked, set.service_name, set.parent_name)
                                        }
                                        sx={{
                                            flexShrink: 0,
                                        }}
                                    />
                                </Box>
                            ))}
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
        },
    });

export { AccountNotifications };
